import React, { FC, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { CentsDataGridServer } from 'components/common/dataGrid/dataGridServer';
import { buildAuditTrailRows, getAuditTrailRowCount } from 'utils/entitlements/buildAuditTrailRows';
import { useEntitlementAuditTrail } from 'api/queries/useEntitlementAuditTrail';
import { AUDIT_TRAIL_PAGE_SIZE } from 'constants/entitlements/entitlements';
import { StoreSummary } from 'types/entitlements/entitlements';
import {
  ALL_STORES,
  AUDIT_TRAIL_COLUMNS,
  AUDIT_TRAIL_GRID_COMPONENTS,
  AUDIT_TRAIL_GRID_COMPONENTS_PROPS,
} from './auditTrailGridConfig';

interface EntitlementAuditTrailProps {
  businessId: number;
  stores: StoreSummary[];
  /** Grants and revocations made on this tab so far; each one sends the trail back to page 1. */
  changeCount: number;
}

/**
 * Every grant and revocation of the business, newest first, 50 a page (COS-4957, backend COS-4953).
 * Optionally narrowed to one store. Read-only: it answers "why does this store hold what it holds".
 */
export const EntitlementAuditTrail: FC<EntitlementAuditTrailProps> = ({
  businessId,
  stores,
  changeCount,
}) => {
  // The grid counts pages from 0; the API counts them from 1.
  const [pageIndex, setPageIndex] = useState(0);
  const [storeFilter, setStoreFilter] = useState<string>(ALL_STORES);

  const page = pageIndex + 1;
  const storeId = storeFilter === ALL_STORES ? undefined : Number(storeFilter);

  const { data, isLoading, isFetching, isError } = useEntitlementAuditTrail({
    businessId,
    page,
    storeId,
  });

  const rows = useMemo(
    () => buildAuditTrailRows(stores, data?.records ?? []),
    [stores, data?.records],
  );
  const rowCount = getAuditTrailRowCount(data?.page ?? page, rows.length, data?.hasMore ?? false);
  const sortedStores = useMemo(
    () => [...stores].sort((left, right) => left.name.localeCompare(right.name)),
    [stores],
  );

  // A change made on this tab is the newest record, so it shows on page 1. The store filter stays.
  useEffect(() => {
    setPageIndex(0);
  }, [changeCount]);

  // While a page loads, the grid still counts from the previous response (another page, or another
  // store), so paging on it could skip records or ask for a page that does not exist.
  const handlePageChange = ({ page: nextPageIndex }: { page: number }): void => {
    if (!isFetching) {
      setPageIndex(nextPageIndex);
    }
  };

  const handleStoreChange = (value: string): void => {
    setStoreFilter(value);
    setPageIndex(0);
  };

  return (
    // Padding, not margin: this is the last block in the page's scroll container, which drops a
    // trailing margin and would leave the table flush with the bottom edge.
    <Box sx={{ mt: 4, pb: 4 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" noWrap sx={{ flexShrink: 0 }}>
          Change history
        </Typography>

        <TextField
          select
          size="small"
          label="Store"
          value={storeFilter}
          onChange={(event) => handleStoreChange(event.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value={ALL_STORES}>All stores</MenuItem>
          {sortedStores.map(({ id, name }) => (
            <MenuItem key={id} value={String(id)}>
              {name}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {isError ? (
        <Alert severity="error">Failed to load the change history.</Alert>
      ) : (
        <CentsDataGridServer
          tableName="ENTITLEMENT_AUDIT_TRAIL"
          data={rows}
          columns={AUDIT_TRAIL_COLUMNS}
          isLoading={isLoading || isFetching}
          rowCount={rowCount}
          page={pageIndex}
          pageSize={AUDIT_TRAIL_PAGE_SIZE}
          onPageChange={handlePageChange}
          components={AUDIT_TRAIL_GRID_COMPONENTS}
          componentsProps={AUDIT_TRAIL_GRID_COMPONENTS_PROPS}
        />
      )}
    </Box>
  );
};
